import { useUpdateUserDefineds } from 'api/user-defined/service';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const FieldItem = ({ fieldName, index, type, category, displayValue, canAdd, onInputChange, onInputBlur, onDeleteField, isTheme2 = false, inputStyle, inputClassName }) => {
    const labelFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : undefined;
    const inputFontSize = isTheme2 ? THEME2_SIDEBAR_NAV_FONT_SIZE_VAR : '14px';

    return (
        <div 
            className="form-group"
            style={{
                transition: 'all 0.2s ease',
                padding: '8px',
                margin: '4px 0',
                border: '1px solid transparent',
                userSelect: 'none',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <label style={{ margin: 0, ...(labelFontSize ? { fontSize: labelFontSize } : {}) }}>UDF{index + 1}</label>
            </div>
            <div style={{ position: 'relative' }}>
                <input
                    style={{
                        paddingRight: canAdd ? '35px' : '10px',
                        ...(isTheme2 ? inputStyle : { fontSize: inputFontSize }),
                    }}
                    type="text"
                    className={inputClassName ? inputClassName(false) : 'form-control'}
                    placeholder={`Enter field name...`}
                    value={displayValue}
                    onChange={(e) => onInputChange(type, category, fieldName, e.target.value)}
                    onBlur={(e) => onInputBlur(type, category, fieldName, e.target.value)}
                    disabled={!canAdd}
                />
                {canAdd && (
                    <button
                        type="button"
                        style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            fontSize: inputFontSize,
                            cursor: 'pointer',
                            padding: '0',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={() => onDeleteField(type, category, fieldName)}
                    >
                        <i className="tim-icons icon-simple-remove"></i>
                    </button>
                )}
            </div>
        </div>
    );
};

const SUserDefinedFieldsTabs = forwardRef(({ permissions, activeColorStyle, initialData, isTheme2 = false, inputStyle, inputClassName }, ref) => {
    const userDefineds = initialData; // initialData is expected to be { results: [...] }
    const updateUserDefinedsMutation = useUpdateUserDefineds();

    const getFormattedData = (type) => {
        let dataToSave = {};
        if (type === 'accounts') dataToSave = { ...accountsData };
        else if (type === 'deals') dataToSave = { ...dealsData };
        else if (type === 'inventory') dataToSave = { ...inventoryData };

        const currentFieldValues = fieldValues[type] || {};
        Object.keys(currentFieldValues).forEach(category => {
            if (dataToSave[category]) {
                const newCat = {};
                Object.keys(currentFieldValues[category]).forEach(key => {
                    const val = currentFieldValues[category][key];
                    if (key.startsWith('field_empty_')) {
                        if (val.trim() !== '') newCat[val] = '';
                    } else {
                        if (val.trim() !== '') newCat[val] = '';
                        else delete newCat[key];
                    }
                });
                dataToSave[category] = newCat;
            }
        });
        return dataToSave;
    };

    useImperativeHandle(ref, () => ({
        getData: () => ({
            results: [
                getFormattedData('accounts'),
                getFormattedData('deals'),
                getFormattedData('inventory')
            ]
        })
    }));
    
    // Permission checks - for Superadmin onboaridng we assume true or based on props
    const hasAccountsReadPermission = true;
    const hasAccountsAddPermission = true;
    const hasDealsReadPermission = true;
    const hasDealsAddPermission = true;
    const hasInventoryReadPermission = true;
    const hasInventoryAddPermission = true;
    
    const availableTabs = ["ACCOUNTS", "DEALS", "INVENTORY"];
    
    const [activeTab, setActiveTab] = useState("ACCOUNTS");
    const [accountsData, setAccountsData] = useState({});
    const [dealsData, setDealsData] = useState({});
    const [inventoryData, setInventoryData] = useState({});
    const [originalData, setOriginalData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [fieldValues, setFieldValues] = useState({});

    
    useEffect(() => {
        if (userDefineds?.results) {
            const newOriginalData = {};
            const newFieldValues = {};
            userDefineds.results.forEach(item => {
                newOriginalData[item.type] = item;
                Object.keys(item).forEach(category => {
                    if (category !== 'id' && category !== 'type' && category !== 'created_at' && category !== 'updated_at') {
                        if (!newFieldValues[item.type]) newFieldValues[item.type] = {};
                        if (!newFieldValues[item.type][category]) newFieldValues[item.type][category] = {};
                        Object.keys(item[category] || {}).forEach(fieldName => {
                            if (!newFieldValues[item.type][category][fieldName]) {
                                newFieldValues[item.type][category][fieldName] = fieldName.startsWith('field_') ? '' : fieldName;
                            }
                        });
                    }
                });
                switch (item.type) {
                    case 'accounts': setAccountsData(item); break;
                    case 'deals': setDealsData(item); break;
                    case 'inventory': setInventoryData(item); break;
                    default: break;
                }
            });
            setOriginalData(newOriginalData);
            setFieldValues(newFieldValues);
        }
    }, [userDefineds]);

    const handleInputChange = (type, category, fieldName, newValue) => {
        setFieldValues(prev => {
            const newValues = { ...prev };
            if (newValues[type]?.[category]) {
                const newCategoryValues = { ...newValues[type][category] };
                if (newValue.trim() === '') delete newCategoryValues[fieldName];
                else newCategoryValues[fieldName] = newValue;
                newValues[type] = { ...newValues[type], [category]: newCategoryValues };
            }
            return newValues;
        });
    };

    const handleInputBlur = (type, category, fieldName, newValue) => {
        if (newValue.trim() === '') {
            setFieldValues(prev => {
                const updated = { ...prev };
                if (updated[type]?.[category]) {
                    const catVals = { ...updated[type][category] };
                    delete catVals[fieldName];
                    updated[type] = { ...updated[type], [category]: catVals };
                }
                return updated;
            });
            return;
        }

        // Simplfied for Superadmin
        const updateDataFn = (prev) => {
            if (prev[category] && typeof prev[category] === 'object') {
                const categoryData = { ...prev[category] };
                categoryData[fieldName] = newValue;
                return { ...prev, [category]: categoryData };
            }
            return prev;
        };

        if (type === 'accounts') setAccountsData(updateDataFn);
        else if (type === 'deals') setDealsData(updateDataFn);
        else if (type === 'inventory') setInventoryData(updateDataFn);
    };

    const handleAddField = (type, category) => {
        const timestamp = Date.now();
        const newFieldName = `field_empty_${timestamp}`;
        setFieldValues(prev => ({
            ...prev,
            [type]: {
                ...prev[type] || {},
                [category]: { ...prev[type]?.[category] || {}, [newFieldName]: '' }
            }
        }));
    };

    const handleDeleteField = (type, category, fieldName) => {
        setFieldValues(prev => {
            const updated = { ...prev };
            if (updated[type]?.[category]) {
                const catVals = { ...updated[type][category] };
                delete catVals[fieldName];
                updated[type] = { ...updated[type], [category]: catVals };
            }
            return updated;
        });
    };

    const handleSave = () => {
        // Logic similar to original, use updateUserDefinedsMutation
        let dataToSave = {};
        if (activeTab === 'ACCOUNTS') dataToSave = { ...accountsData };
        else if (activeTab === 'DEALS') dataToSave = { ...dealsData };
        else if (activeTab === 'INVENTORY') dataToSave = { ...inventoryData };

        const currentFieldValues = fieldValues[activeTab.toLowerCase()] || {};
        Object.keys(currentFieldValues).forEach(category => {
            if (dataToSave[category]) {
                const newCat = {};
                Object.keys(currentFieldValues[category]).forEach(key => {
                    const val = currentFieldValues[category][key];
                    if (key.startsWith('field_empty_')) {
                        if (val.trim() !== '') newCat[val] = '';
                    } else {
                        if (val.trim() !== '') newCat[val] = '';
                        else delete newCat[key];
                    }
                });
                dataToSave[category] = newCat;
            }
        });

        const record = userDefineds?.results?.find(i => i.type === activeTab.toLowerCase());
        if (record) {
            updateUserDefinedsMutation.mutate({ id: record.id, data: dataToSave });
        }
    };

    const renderCategoryFields = (data, type, category, title) => {
        const fieldNames = Object.keys(fieldValues[type]?.[category] || {});
        return (
            <div style={{ width: 'calc(20% - 10px)' }}>
                <div className="card shadow-none border">
                    <div className="card-header font-weight-bold p-2">{title}</div>
                    <div className="card-body p-2">
                        {fieldNames.map((name, i) => (
                            <FieldItem 
                                key={name} fieldName={name} index={i} type={type} category={category} 
                                displayValue={fieldValues[type][category][name] || (name.startsWith('field_empty_') ? '' : name)}
                                canAdd={true} onInputChange={handleInputChange} onInputBlur={handleInputBlur} onDeleteField={handleDeleteField}
                                isTheme2={isTheme2} inputStyle={inputStyle} inputClassName={inputClassName}
                            />
                        ))}
                        <button className="btn btn-link btn-sm w-100" onClick={() => handleAddField(type, category)}>+ Add</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="col-md-12">
            <Nav tabs className="border-0 mb-3">
                {availableTabs.map(tab => (
                    <NavItem key={tab}>
                        <NavLink href="#" className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>{tab}</NavLink>
                    </NavItem>
                ))}
            </Nav>
            <TabContent activeTab={activeTab}>
                <TabPane tabId={activeTab}>
                    <div className="d-flex" style={{ gap: 10 }}>
                        {renderCategoryFields({}, activeTab.toLowerCase(), 'currency', 'CURRENCY')}
                        {renderCategoryFields({}, activeTab.toLowerCase(), 'datetime', 'DATETIME')}
                        {renderCategoryFields({}, activeTab.toLowerCase(), 'numeric', 'NUMERIC')}
                        {renderCategoryFields({}, activeTab.toLowerCase(), 'percentage', 'PERCENTAGE')}
                        {renderCategoryFields({}, activeTab.toLowerCase(), 'text', 'TEXT')}
                    </div>
                    {/* Internal save button removed for global update in Institution.js */}
                </TabPane>
            </TabContent>
        </div>
    );
});

export default SUserDefinedFieldsTabs;
